import { Form, Formik } from "formik";
import { useMemo } from "react";
import { Link } from "react-router-dom";

import {
  Card,
  CardHeader,
  IconButton,
  CardContent,
  makeStyles,
  MenuItem
} from "@material-ui/core";
import { Delete } from "@material-ui/icons";
import { Alert } from "@material-ui/lab";

import CSVUploadLimitError from "src/components/CSVUploadLimitError";
import { useTenant } from "src/components/LoggedInAdminProvider";
import { useSubaccount } from "src/components/SubaccountProvider";
import { FormikSelect } from "src/components/form/Select";
import SubmitButton from "src/components/form/SubmitButton";
import {
  getCampaign_campaign,
  getCampaign_campaign_vanIntegration
} from "src/graphql/generated/getCampaign";
import CsvPreview from "src/pages/OptOuts/CsvPreview";
import {
  CSVMetadata,
  CSVMetadataValid,
  DEFAULT_UPLOAD_ROW_LIMIT
} from "src/util/csv";
import { vanModeFriendlyName } from "src/util/van";
import yup from "src/util/yup";

const useStyles = makeStyles(() => ({
  vanIntegrationName: {
    fontWeight: "bold"
  },
  vanIntegrationMode: { fontWeight: "bold" },
  columnSelect: {
    marginTop: "20px"
  },
  submitButton: {
    marginTop: "20px"
  }
}));

const VAN_ID_DEFAULT_COLUMN = "vanid";
const PHONE_NUMBER_DEFAULT_COLUMN = "phonenumber";

function NoVanIntegrationWarning({
  campaignId
}: {
  campaignId: string;
}): JSX.Element {
  const { subaccountLink } = useSubaccount()!;

  return (
    <Alert severity="warning">
      This campaign is not linked to an integration. If you&apos;d like to sync{" "}
      information back to NGP VAN, please add one in the{" "}
      <Link to={subaccountLink(`/campaigns/${campaignId}/integration`)}>
        integration page
      </Link>
      .
    </Alert>
  );
}

function VanIntegrationInfo({
  campaignId,
  vanIntegration
}: {
  campaignId: string;
  vanIntegration: getCampaign_campaign_vanIntegration;
}): JSX.Element {
  const classes = useStyles();
  const { subaccountLink } = useSubaccount()!;

  return (
    <Alert severity="info">
      These contacts will be linked to{" "}
      <span className={classes.vanIntegrationName}>
        {vanIntegration.vanIntegration.name}
      </span>{" "}
      (
      <span className={classes.vanIntegrationMode}>
        {vanModeFriendlyName(vanIntegration.databaseMode)}
      </span>
      ). To link these contacts to a different integration, go to the{" "}
      <Link to={subaccountLink(`/campaigns/${campaignId}/integration`)}>
        integration page
      </Link>
      . This cannot be changed after the contacts have been imported.
    </Alert>
  );
}

function normalizeColumnName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function matchColumnName(
  metadata: CSVMetadataValid,
  normalizedName: string
): string | undefined {
  return metadata.columns.find(
    col => normalizeColumnName(col) === normalizedName
  );
}

function hasLikelyVanIdColumn(metadata: CSVMetadataValid): boolean {
  return !!matchColumnName(metadata, VAN_ID_DEFAULT_COLUMN);
}

function ColumnMappingForm({
  csvMetadata,
  campaign,
  onStartImport
}: {
  campaign: getCampaign_campaign;
  csvMetadata: CSVMetadataValid;
  onStartImport: ({
    phoneNumberColumn,
    vanIdColumn
  }: {
    phoneNumberColumn: string;
    vanIdColumn?: string;
  }) => void;
}): JSX.Element {
  const classes = useStyles();

  const defaultPhoneNumberColumn = useMemo(
    () => matchColumnName(csvMetadata, PHONE_NUMBER_DEFAULT_COLUMN),
    [csvMetadata]
  );
  const defaultVanIdColumn = useMemo(
    () => matchColumnName(csvMetadata, VAN_ID_DEFAULT_COLUMN),
    [csvMetadata]
  );
  const needsVanIdMapping = !!campaign.vanIntegration;

  return (
    <Formik
      initialValues={{
        phoneNumberColumn: defaultPhoneNumberColumn || "",
        ...(needsVanIdMapping ? { vanIdColumn: defaultVanIdColumn || "" } : {})
      }}
      onSubmit={onStartImport}
      validationSchema={yup.object().shape({
        phoneNumberColumn: yup.string().required("Required"),
        ...(needsVanIdMapping
          ? { vanIdColumn: yup.string().required("Required") }
          : {})
      })}
    >
      <Form>
        <FormikSelect
          name="phoneNumberColumn"
          label="Phone Number Column"
          fullWidth
          className={classes.columnSelect}
        >
          {csvMetadata.columns.map(col => (
            <MenuItem key={col} value={col}>
              {col}
            </MenuItem>
          ))}
        </FormikSelect>

        {!needsVanIdMapping && hasLikelyVanIdColumn(csvMetadata) ? (
          <NoVanIntegrationWarning campaignId={campaign.id} />
        ) : null}
        {needsVanIdMapping ? (
          <>
            <FormikSelect name="vanIdColumn" label="VAN ID Column" fullWidth>
              {csvMetadata.columns.map(col => (
                <MenuItem key={col} value={col}>
                  {col}
                </MenuItem>
              ))}
            </FormikSelect>

            <VanIntegrationInfo
              campaignId={campaign.id}
              vanIntegration={campaign.vanIntegration!}
            />
          </>
        ) : null}
        <SubmitButton className={classes.submitButton}>
          Import contacts
        </SubmitButton>
      </Form>
    </Formik>
  );
}

export default function ParsedFile({
  campaign,
  fileName,
  metadata,
  onClearFile,
  onStartImport
}: {
  campaign: getCampaign_campaign;
  fileName: string;
  metadata: CSVMetadata;
  onClearFile: () => void;
  onStartImport: ({
    phoneNumberColumn,
    vanIdColumn
  }: {
    phoneNumberColumn: string;
    vanIdColumn?: string;
  }) => void;
}): JSX.Element {
  const tenant = useTenant();
  const uploadRowLimit = tenant?.csvUploadLimitRows || DEFAULT_UPLOAD_ROW_LIMIT;
  return (
    <Card>
      <CardHeader
        action={
          <IconButton onClick={onClearFile}>
            <Delete />
          </IconButton>
        }
        title={fileName}
      />
      <CardContent>
        {metadata.valid ? (
          metadata.rows <= uploadRowLimit ? (
            <div>
              <CsvPreview csvMetadata={metadata} />
              <ColumnMappingForm
                csvMetadata={metadata}
                campaign={campaign}
                onStartImport={onStartImport}
              />
            </div>
          ) : (
            <CSVUploadLimitError uploadRowLimit={uploadRowLimit} />
          )
        ) : (
          <>
            <p>
              <strong>Invalid CSV</strong>
            </p>
            <ul>
              {metadata.errors.map(e => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </>
        )}
      </CardContent>
    </Card>
  );
}
